import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { useMutation } from 'react-query';

import { useDebounce } from 'react-use';
import { SubjectLines } from '../../lib/api/types';
import { ContactService, SubmitContactForm } from '../../lib/contact-form/api';
import ContactForm from '../Contact/Contact';
import { messageTypes } from '../Contact/contact.utils';
import ContactFormSuccess from '../Contact/Success';
import WithHelpCenterChrome, {
  WithHelpCenterChromeOwnProps,
} from '../WithHelpCenterChrome';
import WithNavigationTopBar from '../WithNavigationTopBar';

export type ContactPageProps = WithHelpCenterChromeOwnProps & {
  subjectLines: SubjectLines;
  helpCenterId: number;
  isEmbedded?: boolean;
};

const ContactPage = ({ isEmbedded, ...props }: ContactPageProps) => {
  const [showComponent, setShowComponent] = useState<'contact' | 'success'>(
    'contact',
  );
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [iframeHeight, setIframeHeight] = useState(0);

  const contactRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const submitContactForm = useMutation(ContactService.submitContactForm);

  const handleSubmit = async (contactForm: SubmitContactForm) => {
    try {
      await submitContactForm.mutateAsync(contactForm, {
        onSuccess: () => setShowComponent('success'),
        onError: () => setErrorMessage(t('errorSendRequest')),
      });
    } catch (error) {
      setErrorMessage(t('errorSendRequest'));
    }
  };

  useEffect(() => {
    // If the page isn't embedded get the current url
    if (window.self === window.top) {
      setCurrentUrl(window.location.href);
      return;
    }

    const handleMessages = (event: MessageEvent) => {
      if (event.data.type === messageTypes.ParentUrl) {
        setCurrentUrl(event.data.parentUrl);
      }
    };

    const handleResize = () => {
      setIframeHeight(contactRef.current?.clientHeight ?? 0);
    };

    // Listen to messages from the parent window
    window.addEventListener('message', handleMessages);

    // Add observer to resize the iframe when the content changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (contactRef.current) {
      resizeObserver.observe(contactRef.current);
    }

    // Send a message to the parent window, notifying that the page is ready
    window.parent.postMessage({ type: messageTypes.FormLoaded }, '*');

    return () => {
      window.removeEventListener('message', handleMessages);
      resizeObserver.disconnect();
    };
  }, []);

  useDebounce(
    () => {
      // Send a message to the parent window, sending the current height of the iframe
      window.parent.postMessage(
        {
          type: messageTypes.AdjustIframeHeight,
          height: iframeHeight,
        },
        '*',
      );
    },
    50,
    [iframeHeight],
  );

  const content = (
    <div ref={contactRef}>
      {showComponent === 'contact' && (
        <ContactForm
          subjectLines={props.subjectLines}
          onSubmit={handleSubmit}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          currentUrl={currentUrl}
          isEmbedded={!!isEmbedded}
        />
      )}
      {showComponent === 'success' && (
        <ContactFormSuccess
          helpCenterName={props.helpCenterName}
          onLeaveAnotherMessageClick={() => {
            setShowComponent('contact');
          }}
        />
      )}
    </div>
  );

  return isEmbedded ? (
    content
  ) : (
    <WithHelpCenterChrome {...props} hideContactLinkInFooter>
      {!isEmbedded && (
        <WithNavigationTopBar
          {...props}
          isSearchActivated={false}
          levels={[
            {
              title: t('contactUs'),
            },
          ]}
        />
      )}
      {content}
    </WithHelpCenterChrome>
  );
};

export default ContactPage;
